import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { portalApi } from '@/services/portal-api';
import { PdfAnnotationViewer } from '@/components/portal/PdfAnnotationViewer';
import { AprovacaoBar } from '@/components/portal/AprovacaoBar';
import { Anotacao } from '@/components/portal/AnnotationPin';

interface ApreciacaoData {
  id: string;
  numero: string;
  titulo: string;
  revisao: number;
  maquina: { nome: string; tipo: string };
  projeto: { nome: string };
  compartilhamento: {
    id: string;
    status: string;
    dataCompartilhamento: string;
    dataResposta: string | null;
    observacoes: string | null;
  };
}

export function PortalApreciacaoReview() {
  const { id } = useParams<{ id: string }>();
  const [apreciacao, setApreciacao] = useState<ApreciacaoData | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [anotacoes, setAnotacoes] = useState<Anotacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregarDados = useCallback(async () => {
    if (!id) return;

    try {
      // Carregar apreciacao e anotacoes
      const [aprecRes, anotRes] = await Promise.all([
        portalApi.buscarApreciacao(id),
        portalApi.listarAnotacoes(id),
      ]);

      if (aprecRes.success && aprecRes.data) {
        setApreciacao(aprecRes.data);
      } else {
        setError(aprecRes.message || 'Erro ao carregar apreciacao');
        return;
      }

      if (anotRes.success && anotRes.data) {
        setAnotacoes(anotRes.data);
      }

      // Carregar PDF (blob) separadamente
      const pdfBlob = await portalApi.obterPdfBlob(id);
      if (pdfBlob) {
        setPdfUrl(URL.createObjectURL(pdfBlob));
      }
    } catch (err: any) {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const handleCreateAnnotation = async (dados: { pagina: number; posX: number; posY: number; texto: string; tipo: string }) => {
    if (!id) return;
    const res = await portalApi.criarAnotacao(id, dados);
    if (res.success && res.data) {
      setAnotacoes(prev => [...prev, res.data]);
    }
  };

  const handleReplyAnnotation = async (anotacaoId: string, texto: string) => {
    const res = await portalApi.responderAnotacao(anotacaoId, texto);
    if (res.success && res.data) {
      setAnotacoes(prev => prev.map(a =>
        a.id === anotacaoId
          ? { ...a, respostas: [...a.respostas, res.data] }
          : a
      ));
    }
  };

  const handleResolveAnnotation = async (anotacaoId: string) => {
    const res = await portalApi.resolverAnotacao(anotacaoId);
    if (res.success && res.data) {
      setAnotacoes(prev => prev.map(a =>
        a.id === anotacaoId ? { ...a, resolvido: res.data.resolvido } : a
      ));
    }
  };

  const handleAprovar = async (observacoes?: string) => {
    if (!id) return;
    const res = await portalApi.aprovarApreciacao(id, observacoes);
    if (res.success) {
      setApreciacao(prev => prev ? {
        ...prev,
        compartilhamento: { ...prev.compartilhamento, status: 'aprovado', dataResposta: new Date().toISOString() }
      } : null);
    }
  };

  const handleSolicitarRevisao = async (observacoes: string) => {
    if (!id) return;
    const res = await portalApi.solicitarRevisao(id, observacoes);
    if (res.success) {
      setApreciacao(prev => prev ? {
        ...prev,
        compartilhamento: { ...prev.compartilhamento, status: 'revisao_solicitada', dataResposta: new Date().toISOString(), observacoes }
      } : null);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = `apreciacao-${apreciacao?.numero || id}.pdf`;
      a.click();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Carregando documento...</p>
        </div>
      </div>
    );
  }

  if (error || !apreciacao) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'Apreciacao nao encontrada'}</p>
          <Link to="/portal/apreciacoes" className="text-blue-600 hover:text-blue-700 text-sm">
            Voltar para lista
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top info bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center gap-4">
        <Link to="/portal/apreciacoes" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-xs font-mono text-gray-500">{apreciacao.numero}</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{apreciacao.titulo}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {apreciacao.maquina.nome} | Rev. {apreciacao.revisao} | {apreciacao.projeto.nome}
          </p>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 min-h-0">
        {pdfUrl ? (
          <PdfAnnotationViewer
            pdfUrl={pdfUrl}
            anotacoes={anotacoes}
            onCreateAnnotation={handleCreateAnnotation}
            onReplyAnnotation={handleReplyAnnotation}
            onResolveAnnotation={handleResolveAnnotation}
            onDownload={handleDownload}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">PDF nao disponivel</p>
              <p className="text-xs text-gray-400 mt-1">Solicite a exportacao do PDF ao responsavel</p>
            </div>
          </div>
        )}
      </div>

      {/* Approval Bar */}
      <AprovacaoBar
        status={apreciacao.compartilhamento.status}
        onAprovar={handleAprovar}
        onSolicitarRevisao={handleSolicitarRevisao}
      />
    </div>
  );
}
